/* ============================================================================
   TABLERO — las horas, de un vistazo

   POR QUÉ ES UNA PANTALLA Y NO UN INFORME MÁS

   El informe responde «¿cuántas horas voló cada piloto en marzo?». El tablero
   responde **«¿cómo viene la operación?»**: un período, seis números y los
   primeros renglones de cada corte. Es la pantalla que se abre al entrar para
   ver si algo se movió, no la que se manda por correo.

   ═══ LOS NÚMEROS SON LOS MISMOS, NO UNOS PARECIDOS ═══

   Todo sale de `src/domain/estadisticas.js` —las mismas funciones que el
   informe— y con el mismo rango de fechas. Un tablero que calculara sus propios
   totales sería la segunda definición de «horas de vuelo», y el día que las dos
   no coincidan nadie va a saber cuál creer.

   Y la procedencia va abajo, como en el informe: **un total sin decir qué contó
   es un número en el que hay que confiar sin motivo**.
   ========================================================================== */
import { useMemo } from "react";

import {
  SIN_EMPRESA,
  horasPorAeronave,
  horasPorCliente,
  horasPorEmpresa,
  horasPorPiloto,
  totalesDelPeriodo,
} from "../../domain/estadisticas.js";

const numero = (n, decimales = 1) =>
  Number(n || 0).toLocaleString("es-CL", { minimumFractionDigits: decimales, maximumFractionDigits: decimales });

function Cifra({ etiqueta, valor, unidad = "", detalle = null }) {
  return (
    <div className="ges__cifra">
      <span className="ges__cifra-etiqueta">{etiqueta}</span>
      <span className="ges__cifra-valor">
        {valor}
        {unidad && <small>{unidad}</small>}
      </span>
      {detalle && <span className="ges__cifra-detalle">{detalle}</span>}
    </div>
  );
}

/** Los primeros renglones de un corte, con el resto sumado. */
function Corto({ titulo, tabla, vacio }) {
  const filas = (tabla?.filas || []).slice(0, 5);
  const restoHoras = (tabla?.filas || []).slice(5).reduce((s, f) => s + f.horas, 0);

  return (
    <section className="ges__corto">
      <h4 className="ges__corto-titulo">{titulo}</h4>
      {filas.length === 0 ? (
        <p className="ges__corto-vacio">{vacio}</p>
      ) : (
        <ul className="ges__corto-lista">
          {filas.map((f) => (
            <li key={String(f.clave)}>
              <span className="ges__corto-nombre">{f.nombre}</span>
              <span className="ges__corto-horas">{numero(f.horas)} h</span>
            </li>
          ))}
          {restoHoras > 0 && (
            <li className="ges__corto-resto">
              <span className="ges__corto-nombre">
                y {(tabla.filas.length - 5)} más
              </span>
              <span className="ges__corto-horas">{numero(restoHoras)} h</span>
            </li>
          )}
        </ul>
      )}
    </section>
  );
}

export default function Tablero({
  bitacora = [],
  aeronaves = [],
  pilotos = [],
  empresas = [],
  clientes = [],
  desde = null,
  hasta = null,
}) {
  const datos = useMemo(() => {
    const opciones = { desde, hasta };
    return {
      totales: totalesDelPeriodo(bitacora, opciones),
      porAeronave: horasPorAeronave(bitacora, aeronaves, opciones),
      porPiloto: horasPorPiloto(bitacora, pilotos, { ...opciones, empresas }),
      porEmpresa: horasPorEmpresa(bitacora, empresas, { ...opciones, clientes }),
      porCliente: horasPorCliente(bitacora, clientes, opciones),
    };
  }, [bitacora, aeronaves, pilotos, empresas, clientes, desde, hasta]);

  const { totales } = datos;
  const periodo = desde || hasta
    ? `${desde || "el inicio"} a ${hasta || "hoy"}`
    : "todo el libro";

  const sinEmpresa = datos.porEmpresa.filas.find((f) => f.clave === SIN_EMPRESA) || null;
  const sinCliente = datos.porCliente.sinCliente;

  return (
    <div className="ges__tablero">
      <header className="ges__tablero-cabeza">
        <h3 className="ges__titulo">Tablero</h3>
        <span className="ges__periodo">{periodo}</span>
      </header>

      <div className="ges__cifras">
        <Cifra etiqueta="Horas de vuelo" valor={numero(totales.horas)} unidad="h" />
        <Cifra etiqueta="Horas de motor" valor={numero(totales.horasMotor)} unidad="h" />
        <Cifra etiqueta="Vuelos" valor={numero(totales.vuelos, 0)} />
        <Cifra etiqueta="Días volados" valor={numero(totales.dias, 0)} />
        <Cifra etiqueta="Aterrizajes" valor={numero(totales.aterrizajes, 0)} />
        <Cifra
          etiqueta="Promedio por vuelo"
          valor={totales.vuelos ? numero(totales.horas / totales.vuelos, 2) : "—"}
          unidad={totales.vuelos ? "h" : ""}
        />
      </div>

      <div className="ges__cortos">
        <Corto
          titulo="Por aeronave"
          tabla={datos.porAeronave}
          vacio="Ningún asiento en el período."
        />
        <Corto
          titulo="Por piloto"
          tabla={datos.porPiloto}
          vacio="Ningún asiento con piloto en el período."
        />
        <Corto
          titulo="Por empresa"
          tabla={datos.porEmpresa}
          vacio="Ningún asiento en el período."
        />
        <Corto
          titulo="Por cliente"
          tabla={datos.porCliente}
          vacio="Ningún asiento con cliente en el período."
        />
      </div>

      {/* Lo que el corte no pudo ubicar, dicho. Es la misma regla del informe:
          si hay horas sin empresa o sin cliente, se explica de dónde salen en
          vez de dejar que engrosen una fila sin nombre. */}
      {(sinEmpresa || sinCliente) && (
        <p className="ges__nota">
          {sinEmpresa && (
            <>
              <strong>{numero(sinEmpresa.horas)} h</strong> en vuelos que no dicen para qué
              empresa se volaron{" "}
            </>
          )}
          {sinEmpresa && sinCliente && " · "}
          {sinCliente && (
            <>
              <strong>{numero(sinCliente.horas)} h</strong> sin cliente (traslado propio o
              instrucción)
            </>
          )}
          . Los asientos cargados antes de que el registro lo pidiera entran acá.
        </p>
      )}

      <p className="ges__procedencia">
        Asientos contados: <strong>{totales.procedencia.asientos}</strong> ·
        fuera por discrepancia con el papel:{" "}
        <strong>{totales.procedencia.excluidosPorDiscrepancia}</strong> ·
        sin fecha: <strong>{totales.procedencia.sinFecha}</strong>
      </p>
    </div>
  );
}
